/**
 * `useDeleteProjectFile` mutation hook.
 *
 * Hard-deletes the underlying `project_file` row for upload-origin rows in
 * the merged Library list. Attachment-origin rows go through
 * `useDetachArtifact` instead — the Library DTO funnels two backing tables
 * (project_artifacts + project_files) through one row shape, so the
 * calling page branches on `origin` to pick the right endpoint.
 *
 * Cache invalidation targets list queries via
 * `PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId)` so the Library
 * grid refreshes across all filter/sort/page combinations without
 * unnecessarily clearing attachedIds or detail queries.
 *
 * Toast side effects are intentionally NOT handled here: the calling page
 * (`Library`) owns success/error toasts and modal close, matching
 * `useDetachArtifact`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PROJECT_ARTIFACT_QUERY_KEYS } from '@/entities/project-artifact';
import { deleteProjectFile } from '@/entities/project-file';

interface UseDeleteProjectFileOptions {
  projectId: string;
}

export function useDeleteProjectFile(options: UseDeleteProjectFileOptions) {
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation({
    mutationFn: (fileId: string) => deleteProjectFile(projectId, fileId),
    onSuccess: () => {
      // Invalidate only list queries (all filter/sort/page combos) without
      // clearing attachedIds or detail queries unnecessarily.
      void queryClient.invalidateQueries({
        queryKey: PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId),
      });
    },
  });
}
